"use client";

import { Anchor } from "@mantine/core";
import { usePathname } from "next/navigation";
import { FiBell, FiMessageCircle, FiSmartphone } from "react-icons/fi";
import {
  Badge,
  Button,
  Group,
  LineIcon,
  Modal,
  Stack,
  Text,
} from "@/components/ui/elements";
import {
  AppStoreButton,
  GooglePlayStoreButton,
} from "@/components/ui/parts/appStore";

import {
  APP_STORE_URL,
  PLAY_STORE_URL,
} from "./_function/nativeAppReleaseConstants";

type LineAppLinkPromptModalProps = {
  opened: boolean;
  onClose: () => void;
};

const sectionCardStyle = {
  borderRadius: "12px",
  padding: "14px 16px",
} as const;

const storeBadgeWrapStyle = {
  display: "flex",
  justifyContent: "center",
  gap: "8px",
} as const;

export function LineAppLinkPromptModal({
  opened,
  onClose,
}: LineAppLinkPromptModalProps) {
  const pathname = usePathname();

  const handleLineLogin = () => {
    const pathQuery = `?next=${pathname}`;
    const redirectUri = process.env
      .NEXT_PUBLIC_LINE_LINKING_REDIRECT_URL as string;
    const channelId = process.env.NEXT_PUBLIC_LINE_CHANNEL_ID as string;
    const encodeUri = encodeURIComponent(redirectUri + pathQuery);
    const lineAuthUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&scope=profile%20openid&state=123&client_id=${channelId}&redirect_uri=${encodeUri}&prompt=consent&bot_prompt=normal`;
    window.location.href = lineAuthUrl;
  };

  return (
    <Modal
      opened={opened}
      close={onClose}
      centered
      size="md"
      padding="xl"
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap="md">
        <Badge
          size="sm"
          variant="light"
          color="teal"
          radius="xl"
          fw={600}
          w="fit-content"
          leftSection={<FiBell size={14} aria-hidden />}
          styles={{
            root: {
              paddingLeft: 10,
              paddingRight: 10,
              textTransform: "none",
            },
          }}
        >
          ケアリンクからのお知らせ
        </Badge>

        <div
          style={{
            background: "linear-gradient(135deg, #e0f7fa 0%, #e8f5e9 100%)",
            borderRadius: "16px",
            padding: "20px 16px",
            border: "1px solid #b2dfdb",
          }}
        >
          <Stack gap="sm" align="center">
            <Text
              fw={700}
              ta="center"
              style={{
                fontSize: "22px",
                color: "#00695c",
                lineHeight: 1.45,
              }}
            >
              LINE連携・アプリで
              <br />
              お得な情報を受け取ろう
            </Text>
            <Text size="sm" ta="center" c="dark.6" lh={1.7}>
              応募促進通知などのお知らせが届きやすくなります
            </Text>
          </Stack>
        </div>

        <div
          style={{
            ...sectionCardStyle,
            backgroundColor: "#e0f2f1",
            border: "1px solid #80cbc4",
          }}
        >
          <Stack gap="sm">
            <Group gap="xs" wrap="nowrap">
              <FiMessageCircle size={18} color="#00796b" aria-hidden />
              <Text size="sm" fw={700} c="teal.8">
                LINE連携する
              </Text>
            </Group>
            <Text size="sm" lh={1.7} c="dark.6">
              公式アカウントを友だち追加して通知を受け取れます。
            </Text>
            <Button
              onClick={handleLineLogin}
              radius="sm"
              leftSection={<LineIcon size="1rem" />}
              color="line.0"
              fullWidth
            >
              LINEで連携する
            </Button>
          </Stack>
        </div>

        <div
          style={{
            ...sectionCardStyle,
            backgroundColor: "#e0f2f1",
            border: "1px solid #80cbc4",
          }}
        >
          <Stack gap="sm">
            <Group gap="xs" wrap="nowrap">
              <FiSmartphone size={18} color="#00796b" aria-hidden />
              <Text size="sm" fw={700} c="teal.8">
                アプリをダウンロードする
              </Text>
            </Group>
            <Text size="sm" lh={1.7} c="dark.6">
              Android版・iPhone版どちらもご利用いただけます。
            </Text>
            <div style={storeBadgeWrapStyle}>
              <GooglePlayStoreButton
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
              />
              <AppStoreButton
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
              />
            </div>
          </Stack>
        </div>

        <Group justify="center">
          <Anchor
            component="button"
            type="button"
            onClick={onClose}
            size="sm"
            c="dark.4"
          >
            閉じる
          </Anchor>
        </Group>
      </Stack>
    </Modal>
  );
}
